import React, { useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { inject, observer, Provider } from 'mobx-react';

import Tab from '@material-ui/core/Tab';
import Container from '@material-ui/core/Container';
import Background from 'components/Layout/Background';

import compose from 'helpers/compose';
import Tabs from 'components/CustomTabs/CustomTabs';
import { EditAccountStore } from 'stores/editAccountStore';
import { Params, ACCOUNTS_EDIT_VARIANT, ACCOUNTS_EDIT_QUESTIONNAIRE } from '../accountEditRoutes';
import Target from './Target';
import Deposits from 'pages/Accounts/Edit/components/Deposits';
import ExternalProducts from 'pages/Accounts/Edit/components/ExternalProducts/ExternalProducts';
import ControlButtons from 'components/ControlButtons';
import { FinancesStore } from 'stores/workflowStores/finances/financesStore';
import { RootStore } from 'stores/rootStore';
import { ViewMode } from 'pages/Accounts/Edit/components/viewModes';
import { Permission } from 'helpers/permissions';
import { PersonType, AccountMode } from 'enums';

interface Props {
  editAccountStore?: EditAccountStore;
  rootStore: RootStore;
}

const flowNoExt = [
  { id: 'targets', label: 'finQuestionnary_goalPlan_planGoal' },
  { id: 'deposits', label: 'finQuestionnary_goalPlan_depositGoal' },
];
const flowExt = [
  { id: 'targets', label: 'finQuestionnary_goalPlan_planGoal' },
  { id: 'extProducts', label: 'finQuestionnary_goalPlan_resources' },
  { id: 'deposits', label: 'finQuestionnary_goalPlan_depositGoal' },
];

const Finances: React.FC<Props> = ({ editAccountStore, rootStore }) => {
  const { t } = useTranslation();
  const { accountId } = useParams<Params>();
  const history = useHistory();
  const [financesStore] = useState(new FinancesStore(rootStore, accountId));
  const { currency } = rootStore.stores.editAccountStore;
  const extProductAllowed =
    rootStore.stores.userStore.checkPermissions(Permission.ACCOUNT_EXT_PRODUCT) &&
    financesStore.data?.personType === PersonType.PHYSICAL &&
    editAccountStore?.mode === AccountMode.STANDARD;
  const flow =
    extProductAllowed || financesStore.data?.externalProductData.externalProducts.length ? flowExt : flowNoExt;
  const [tab, setTab] = useState(flow[0].id);

  useEffect(() => {
    (async () => {
      editAccountStore!.setStep(2);
      await financesStore.fetchData();
    })();
  }, [editAccountStore, financesStore]);

  const handleTabChange = (_event: React.ChangeEvent<{}>, value?: string) => {
    if (value !== undefined) {
      setTab(value);
    }
  };

  const back = () => {
    const index = flow.findIndex((step) => step.id === tab);
    if (index === 0) {
      history.push(ACCOUNTS_EDIT_VARIANT(accountId));
    } else {
      setTab(flow[index - 1].id);
    }
  };

  const next = () => {
    const index = flow.findIndex((step) => step.id === tab);
    if (index === flow.length - 1) {
      history.push(ACCOUNTS_EDIT_QUESTIONNAIRE(accountId));
    } else {
      setTab(flow[index + 1].id);
    }
  };

  const backLabel = () => {
    const index = flow.findIndex((step) => step.id === tab);
    return index === 0 ? t('accountStepper_variantChooser') : t(flow[index - 1].label);
  };

  const nextLabel = () => {
    const index = flow.findIndex((step) => step.id === tab);
    return index === flow.length - 1 ? t('accountStepper_investmentQuestionnaire') : t(flow[index + 1].label);
  };

  return financesStore.data ? (
    <Container maxWidth="md">
      <Provider financesStore={financesStore} key={accountId}>
        <Tabs value={tab} onChange={handleTabChange}>
          {flow.map((tab) => (
            <Tab label={t(tab.label)} value={tab.id} key={tab.id} />
          ))}
        </Tabs>

        <Background afterMenu>
          {tab === 'targets' && (
            <Target overview={financesStore.data} currency={currency} mode={editAccountStore!.mode} />
          )}
          {tab === 'extProducts' && (
            <ExternalProducts
              accountId={accountId}
              productsData={financesStore.data.externalProductData}
              currency={currency}
              defaultInflation={financesStore.data.options.defaultInflationRate}
              defaultInflationType={financesStore.data.options.defaultInflationType}
              refresh={financesStore.fetchData}
            />
          )}
          {tab === 'deposits' && (
            <Deposits
              accountId={accountId}
              currency={currency}
              data={financesStore.data.depositData}
              refresh={financesStore.fetchData}
              viewMode={ViewMode.WF}
              defaultInflation={financesStore.data.options.defaultInflationRate}
              defaultInflationType={financesStore.data.options.defaultInflationType}
              mode={editAccountStore!.mode}
            />
          )}
        </Background>

        <ControlButtons
          isSubmitting={false}
          onNext={next}
          onBack={back}
          nextText={nextLabel()}
          backText={backLabel()}
        />
      </Provider>
    </Container>
  ) : null;
};

export default compose(inject('editAccountStore', 'rootStore'), observer)(Finances);
